import { TreeOptions } from "./treeOptions";

/**
 * treeMap function applies a mapping function to each node in a tree structure using Depth-First Search (DFS).
 *
 * @template T - The type of the tree node.
 * @template C - The type of the tree options.
 * @template F - The type of the mapping function that takes a node and its path as arguments and returns an object.
 * @param nodeList - An array of tree nodes to be processed.
 * @param fn - A function that takes a node (excluding its children) and its path, and returns an object.
 * @param options - An object containing the tree options, specifically the keys for `id` and `children` (defaulting to "id" and "children").
 * @returns An array of objects resulting from applying the mapping function to each node in the tree.
 */
export function treeMap<
  T extends object,
  C extends TreeOptions<T>,
  F extends (t: Omit<T, C["children"]> & Record<C["children"], ReturnType<F>[]>, path: T[C["id"]][]) => object,
>(nodeList: T[], fn: F, options: C = { id: "id" as keyof T, children: "children" as keyof T } as C): ReturnType<F>[] {
  function inner(nodeList: T[], parentPath: T[C["id"]][]): any {
    return nodeList.map((node) => {
      const path = [...parentPath, node[options.id]];
      const children = node[options.children] as any as T[];
      if (!children) {
        return fn(node as any, path);
      }
      return fn(
        {
          ...node,
          [options.children]: inner(children, path),
        },
        path
      );
    });
  }

  return inner(nodeList, []);
}
